import React, {useState, useEffect} from 'react'
import {WatchkeepingCertificateRequest, WatchkeepingCertificateResponse} from '../../core/_models'
import {
  uploadWatchkeepingFile,
  fetchWatchkeepingFileBlob,
  deleteWatchkeepingFile,
  createWatchkeepingCertificate,
  updateWatchkeepingCertificate,
  getOnboardingToken,
  saveCrewOnboardingWatchkeeping,
  getCrewOnboardingDraft,
} from '../../core/_requests'
import {toast} from 'react-toastify'
import {DateTextInput} from '../DateTextInput'
import {useAuth} from '../../../auth'
import {showErrorToast} from '../../../../shared/utils/apiErrorMessage'
interface WatchkeepingCertificatesProps {
  crewId: number | null
  watchkeepingCertificates: WatchkeepingCertificateRequest[]
  handleWatchkeepingCertificateChange: (
    index: number,
    field: keyof WatchkeepingCertificateRequest,
    value: string
  ) => void
  fieldErrors: Record<string, string>
  existingWatchkeepingCertificates: WatchkeepingCertificateResponse[]
  onRefresh?: () => void | Promise<void>
}
const certificateDetails = [
  'Deck Watch keeping Regulation II/4',
  'Able Seafarer Deck Regulation II/5 (COP)',
  'Engine Room Watch keeping III/4',
  'Able Seafarer Engine Regulation III/5 (COP)',
]
const MAX_FILE_SIZE = 9 * 1024 * 1024 // 9 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
const WatchkeepingCertificates: React.FC<WatchkeepingCertificatesProps> = ({
  crewId,
  watchkeepingCertificates,
  handleWatchkeepingCertificateChange,
  fieldErrors,
  existingWatchkeepingCertificates,
  onRefresh,
}) => {
  const {isSuperAdmin, hasPermission} = useAuth()
  const isOnboarding = !!getOnboardingToken()
  const canUploadCrewDocument =
    isSuperAdmin || hasPermission('UPLOAD_CREW_DOCUMENT') || isOnboarding
  const canDeleteCrewDocument =
    isSuperAdmin || hasPermission('DELETE_CREW_DOCUMENT') || isOnboarding
  const canViewCrewDocumentFile =
    isSuperAdmin || hasPermission('READ_CREW_DOCUMENT') || isOnboarding
  // Local map: certificateDetails string => File
  const [filesByKey, setFilesByKey] = useState<Record<string, File | null>>({})
  // Track backend id + file-exists per certificateDetails row
  const [idByKey, setIdByKey] = useState<Record<string, number | undefined>>({})
  const [hasFileByKey, setHasFileByKey] = useState<Record<string, boolean>>({})
  const validateFile = (file: File | null) => {
    if (!file) return {isValid: false, message: 'No file selected'}
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return {
        isValid: false,
        message: 'Invalid file type. Only JPEG, PNG, JPG, and PDF are allowed.',
      }
    }
    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        message: 'File is too large. Maximum size is 9MB.',
      }
    }
    return {isValid: true, message: ''}
  }
  // Initialize local maps from existing backend data
  useEffect(() => {
    const nextIds: Record<string, number | undefined> = {}
    const nextHasFile: Record<string, boolean> = {}
    if (existingWatchkeepingCertificates && existingWatchkeepingCertificates.length > 0) {
      existingWatchkeepingCertificates.forEach((c) => {
        if (c.certificateDetails) {
          nextIds[c.certificateDetails] = c.id
          nextHasFile[c.certificateDetails] = !!c.filePath
        }
      })
    } else if (watchkeepingCertificates && watchkeepingCertificates.length > 0) {
      watchkeepingCertificates.forEach((c: any) => {
        if (c.certificateDetails) {
          nextIds[c.certificateDetails] = c.id
          nextHasFile[c.certificateDetails] = !!c.filePath
        }
      })
    }
    setIdByKey(nextIds)
    setHasFileByKey(nextHasFile)
  }, [existingWatchkeepingCertificates, watchkeepingCertificates])
  const handleFileChangeLocal = (key: string, file: File | null) => {
    if (file) {
      const validation = validateFile(file)
      if (!validation.isValid) {
        toast.warn(validation.message)
        return
      }
    }
    setFilesByKey((prev) => ({...prev, [key]: file}))
  }
  const handleSaveFile = async (key: string, index: number) => {
    const file = filesByKey[key]
    if (!file) {
      toast.warn('Please choose a file before saving.')
      return
    }
    if (!crewId && !isOnboarding) {
      toast.warn('Please save Personal Data (crew) first.')
      return
    }
    try {
      let certificateId = idByKey[key]
      if (isOnboarding && !certificateId) {
        const token = getOnboardingToken()
        if (token) {
          await saveCrewOnboardingWatchkeeping(token, watchkeepingCertificates)
          const draft = await getCrewOnboardingDraft(token)
          if (draft && draft.watchkeeping) {
            const matched = draft.watchkeeping.find((c: any) => c.certificateDetails === key)
            if (matched) {
              certificateId = matched.id
              if (onRefresh) {
                await onRefresh()
              }
            }
          }
        }
      }
      // Build payload from the current row values
      const row = watchkeepingCertificates[index]
      const payload: WatchkeepingCertificateRequest = {
        certificateDetails: key,
        certificateNo: row?.certificateNo || null,
        dateOfIssue: row?.dateOfIssue || null,
        placeOfIssue: row?.placeOfIssue || null,
        validUntil: row?.validUntil || null,
      }
      // If no backend row yet, create it now
      if (!certificateId) {
        if (isOnboarding) {
          toast.warn('Could not resolve certificate ID. Please try saving details first.')
          return
        }
        const created = await createWatchkeepingCertificate(crewId, payload)
        certificateId = created.id
      } else {
        // Keep backend details in sync
        if (isOnboarding) {
          // In onboarding, details are saved via saveCrewOnboardingWatchkeeping call above
        } else {
          await updateWatchkeepingCertificate(crewId, certificateId, payload)
        }
      }
      // Upload / update the file for this certificate
      await uploadWatchkeepingFile(crewId, certificateId!, file)
      // Update local maps so UI reflects file presence immediately
      setIdByKey((prev) => ({...prev, [key]: certificateId}))
      setHasFileByKey((prev) => ({...prev, [key]: true}))
      setFilesByKey((prev) => ({...prev, [key]: null}))
      toast.success('Watchkeeping certificate file uploaded.')
    } catch (err) {
      console.error('Failed to upload watchkeeping file', err)
      showErrorToast(err, 'Failed to upload watchkeeping file.', 'create')
    }
  }
  const handlePreviewFile = async (key: string) => {
    if (!crewId && !isOnboarding) {
      toast.warn('Please save Personal Data (crew) first.')
      return
    }
    const id = idByKey[key]
    if (!id || !hasFileByKey[key]) {
      toast.warn('No file available to preview for this certificate.')
      return
    }
    try {
      const blob = await fetchWatchkeepingFileBlob(crewId, id)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      console.error('Failed to preview watchkeeping file', err)
      showErrorToast(err, 'Failed to preview file.', 'load')
    }
  }
  const handleDeleteFile = async (key: string) => {
    if (!crewId && !isOnboarding) {
      toast.warn('Please save Personal Data (crew) first.')
      return
    }
    const id = idByKey[key]
    // No backend row / file yet → just clear local file
    if (!id || !hasFileByKey[key]) {
      setFilesByKey((prev) => ({...prev, [key]: null}))
      setHasFileByKey((prev) => ({...prev, [key]: false}))
      return
    }
    try {
      await deleteWatchkeepingFile(crewId, id)
      setFilesByKey((prev) => ({...prev, [key]: null}))
      setHasFileByKey((prev) => ({...prev, [key]: false}))
      toast.success('Watchkeeping file removed.')
    } catch (err) {
      console.error('Failed to delete watchkeeping file', err)
      showErrorToast(err, 'Failed to delete file.', 'delete')
    }
  }
  const renderFileCell = (key: string, index: number) => {
    const pendingFile = filesByKey[key] ?? null
    const pendingFileName = pendingFile?.name || ''
    const hasServerFile = !!hasFileByKey[key]
    const fileInputId = `watchkeeping-file-input-${index}`
    // ✅ If file already exists on server → only show icons (View + Remove)
    if (hasServerFile) {
      return (
        <div className='d-flex flex-wrap gap-2 align-items-center'>
          {canViewCrewDocumentFile && (
            <button
              type='button'
              className='btn btn-sm btn-icon btn-info'
              title='View file'
              onClick={() => handlePreviewFile(key)}
            >
              <i className='bi bi-eye' />
            </button>
          )}
          {canDeleteCrewDocument && (
            <button
              type='button'
              className='btn btn-sm btn-icon btn-secondary'
              title='Remove file'
              onClick={() => handleDeleteFile(key)}
            >
              <i className='bi bi-trash' />
            </button>
          )}
          {!canViewCrewDocumentFile && !canDeleteCrewDocument && (
            <span className='text-success small'>Uploaded</span>
          )}
        </div>
      )
    }
    if (!canUploadCrewDocument) {
      return <span className='text-muted small'>Not uploaded</span>
    }
    // ✅ No file yet → show styled choose file + Save
    return (
      <div className='d-flex align-items-center gap-2 flex-wrap'>
        <input
          id={fileInputId}
          type='file'
          className='d-none'
          accept='.pdf,.png,.jpg,.jpeg'
          onChange={(e) => handleFileChangeLocal(key, e.target.files?.[0] || null)}
        />
        {!pendingFileName ? (
          <label
            htmlFor={fileInputId}
            className='btn btn-light-primary mb-0'
            style={{
              height: '31px',
              minWidth: '102px',
              padding: '0 0.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              lineHeight: 1,
            }}
          >
            Choose File
          </label>
        ) : (
          <label
            htmlFor={fileInputId}
            className='text-primary small mb-0 text-truncate'
            style={{maxWidth: '160px', cursor: 'pointer'}}
            title={pendingFileName}
          >
            {pendingFileName}
          </label>
        )}
        {pendingFile && (
          <button
            type='button'
            className='btn btn-primary'
            style={{
              height: '31px',
              minWidth: '78px',
              padding: '0 0.75rem',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              lineHeight: 1,
            }}
            onClick={() => handleSaveFile(key, index)}
          >
            Upload
          </button>
        )}
      </div>
    )
  }
  return (
    <div className='container mt-4'>
      <h5 className='fw-bold mb-3'>
        Watch keeping certificates{' '}
        <span className='fw-normal'>
          (For <strong>Rating</strong> only)
        </span>
      </h5>
      <div className='table-responsive'>
        <table className='table table-bordered'>
          <thead className='table-light'>
            <tr>
              <th>Certificate Details</th>
              <th>Certificate No</th>
              <th>Date of Issue</th>
              <th>Place of Issue</th>
              <th>Valid Until</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            {certificateDetails.map((detail, index) => (
              <tr key={index}>
                <td>{detail}</td>
                <td>
                  <input
                    type='text'
                    className={`form-control ${
                      fieldErrors[`watchkeeping_certificateNo_${index}`] ? 'is-invalid' : ''
                    }`}
                    placeholder='Enter Certificate No'
                    value={watchkeepingCertificates[index]?.certificateNo || ''}
                    onChange={(e) =>
                      handleWatchkeepingCertificateChange(index, 'certificateNo', e.target.value)
                    }
                  />
                  {fieldErrors[`watchkeeping_certificateNo_${index}`] && (
                    <div className='invalid-feedback'>
                      {fieldErrors[`watchkeeping_certificateNo_${index}`]}
                    </div>
                  )}
                </td>
                <td>
                  <DateTextInput
                    className={`form-control ${
                      fieldErrors[`watchkeeping_dateOfIssue_${index}`] ? 'is-invalid' : ''
                    }`}
                    value={watchkeepingCertificates[index]?.dateOfIssue || ''}
                    onChange={(val) =>
                      handleWatchkeepingCertificateChange(index, 'dateOfIssue', val)
                    }
                  />
                  {fieldErrors[`watchkeeping_dateOfIssue_${index}`] && (
                    <div className='invalid-feedback'>
                      {fieldErrors[`watchkeeping_dateOfIssue_${index}`]}
                    </div>
                  )}
                </td>
                <td>
                  <input
                    type='text'
                    className={`form-control ${
                      fieldErrors[`watchkeeping_placeOfIssue_${index}`] ? 'is-invalid' : ''
                    }`}
                    placeholder='Enter Place'
                    value={watchkeepingCertificates[index]?.placeOfIssue || ''}
                    onChange={(e) =>
                      handleWatchkeepingCertificateChange(index, 'placeOfIssue', e.target.value)
                    }
                  />
                  {fieldErrors[`watchkeeping_placeOfIssue_${index}`] && (
                    <div className='invalid-feedback'>
                      {fieldErrors[`watchkeeping_placeOfIssue_${index}`]}
                    </div>
                  )}
                </td>
                <td>
                  <DateTextInput
                    className={`form-control ${
                      fieldErrors[`watchkeeping_validUntil_${index}`] ? 'is-invalid' : ''
                    }`}
                    value={watchkeepingCertificates[index]?.validUntil || ''}
                    onChange={(val) =>
                      handleWatchkeepingCertificateChange(index, 'validUntil', val)
                    }
                  />
                  {fieldErrors[`watchkeeping_validUntil_${index}`] && (
                    <div className='invalid-feedback'>
                      {fieldErrors[`watchkeeping_validUntil_${index}`]}
                    </div>
                  )}
                </td>
                <td>{renderFileCell(detail, index)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
export default WatchkeepingCertificates
